import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectIncidentGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { fetchRostersForGroups } from '../../../actions/roleActions';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useIncidentRoster } from './incidentRoster/useIncidentRoster';
import { useResourceSets } from './resourceSetHooks';
import {
  GetIncidentActionResponse,
  useCreateIncidentAction,
  useUpdateIncidentAction,
} from './incidentActionHooks';
import {
  CreateOrgActionReqData,
  GetOrgActionResponse,
  useCreateOrgAction,
  useUpdateOrgAction,
} from './orgActionHooks';

export type ActionFormValues = {
  name: string;
  description: string;
  poc?: string | null;
  briefed_poc?: boolean;
  status?: string | null;
  start_date?: Date | string | null;
  target_date?: Date | string | null;
  actual_date?: Date | string | null;
  resources?: string[];
  checklist: { label: string; checked: boolean }[];
};

export type ActionEntity = GetIncidentActionResponse | GetOrgActionResponse;

export type ActionRow = Pick<
  GetIncidentActionResponse,
  | 'name'
  | 'description'
  | 'checklist'
  | 'archived'
  | 'poc'
  | 'briefed_poc'
  | 'status'
  | 'start_date'
  | 'target_date'
  | 'actual_date'
  | 'parent_id'
  | 'user_name'
  | 'poc_user_name'
>;

const browserUtcOffset = () => -new Date().getTimezoneOffset();

export const orgActionToRow = (action: GetOrgActionResponse): ActionRow => ({
  name: action.name,
  description: action.description,
  checklist: action.checklist,
  archived: action.archived,
  poc: action.poc,
  briefed_poc: action.briefedPoc,
  status: action.status,
  start_date: action.startDate,
  target_date: action.targetDate,
  actual_date: action.actualDate,
  parent_id: action.parentId ?? null,
  user_name: action.userName,
  poc_user_name: action.pocUserName,
});

const toOrgActionRequest = (
  values: ActionFormValues
): CreateOrgActionReqData => ({
  name: values.name,
  description: values.description,
  checklist: values.checklist,
  poc: values.poc,
  briefedPoc: values.briefed_poc,
  status: values.status,
  startDate: values.start_date,
  targetDate: values.target_date,
  actualDate: values.actual_date,
  utcOffset: browserUtcOffset(),
});

/**
 * Abstracts the incident-vs-org differences (POC source, resources picker, and
 * which create/update/archive mutations fire) behind one uniform interface so
 * the Action drawer can stay scope-agnostic. Both sides' hooks are instantiated
 * unconditionally (rules of hooks); the unused side stays inert because its
 * queries are `enabled`-gated and its mutations simply aren't invoked.
 */
export const useScopedActionForm = (scope: IapScope) => {
  const isIncident = scope === INCIDENT;
  const dispatch = useDispatch<any>();

  // Incident side
  const incidentId = useAppSelector(selectIncidentGuid);
  const { data: incidentRoster } = useIncidentRoster();
  const { data: allResourceSets } = useResourceSets();
  const createIncident = useCreateIncidentAction();
  const updateIncident = useUpdateIncidentAction();

  // Org side
  const groupGuid = useSelector(selectGroupGuid);
  const rosters = useSelector(
    (state: any) => (state.app.rostersForGroups as RosterItem[]) || []
  );
  const createOrg = useCreateOrgAction();
  const updateOrg = useUpdateOrgAction();

  useEffect(() => {
    if (!isIncident && groupGuid && rosters.length === 0) {
      dispatch(fetchRostersForGroups([groupGuid]));
    }
  }, [dispatch, isIncident, groupGuid, rosters.length]);

  const pocOptions = useMemo(() => {
    if (isIncident) {
      return (incidentRoster ?? [])
        .filter((u) => u.userName || u.emailAddress)
        .map((u) => ({
          label: u.userName ?? (u.emailAddress as string),
          value: u.userId,
        }));
    }
    return rosters
      .filter((r) => r.group_guid === groupGuid)
      .map((r) => ({ label: r.user_name, value: r.user_guid }));
  }, [isIncident, incidentRoster, rosters, groupGuid]);

  const resourceOptions = useMemo(() => {
    if (!isIncident) return undefined;
    return (allResourceSets ?? []).filter(
      (rs) => rs.incidents?.includes(incidentId) && !rs?.archived
    );
  }, [isIncident, allResourceSets, incidentId]);

  const create = async (values: ActionFormValues) => {
    if (isIncident) {
      return createIncident.mutateAsync({ ...values, archived: false } as any);
    }
    return createOrg.mutateAsync(toOrgActionRequest(values));
  };

  const update = async (entity: ActionEntity, values: ActionFormValues) => {
    if (isIncident) {
      const inc = entity as GetIncidentActionResponse;
      return updateIncident.mutateAsync({
        ...values,
        id: inc.id,
        action_id: inc.action_id,
      } as any);
    }
    const org = entity as GetOrgActionResponse;
    return updateOrg.mutateAsync({
      ...toOrgActionRequest(values),
      actionId: org.actionId,
      parentId: org.parentId,
      eventId: org.eventId,
      severity: org.severity,
      notificationTemplateId: org.notificationTemplateId,
      archived: false,
    });
  };

  const archive = async (entity: ActionEntity, archived: boolean) => {
    if (isIncident) {
      const inc = entity as GetIncidentActionResponse;
      return updateIncident.mutateAsync({ ...inc, archived } as any);
    }
    const org = entity as GetOrgActionResponse;
    return updateOrg.mutateAsync({
      actionId: org.actionId,
      name: org.name,
      description: org.description,
      checklist: org.checklist ?? [],
      poc: org.poc,
      briefedPoc: org.briefedPoc,
      status: org.status,
      startDate: org.startDate,
      targetDate: org.targetDate,
      actualDate: org.actualDate,
      parentId: org.parentId,
      eventId: org.eventId,
      severity: org.severity,
      notificationTemplateId: org.notificationTemplateId,
      utcOffset: browserUtcOffset(),
      archived,
    });
  };

  const isSaving = isIncident
    ? createIncident.isPending || updateIncident.isPending
    : createOrg.isPending || updateOrg.isPending;

  return {
    pocOptions,
    resourceOptions,
    showResources: isIncident,
    create,
    update,
    archive,
    isSaving,
  };
};
